import { type ReactNode, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, NavLink, useNavigate } from "react-router";

import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import type { AppUser } from "@/features/board/types";
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

type AppShellProps = {
  children: ReactNode;
  user: AppUser | null;
};

const navClassName = ({ isActive }: { isActive: boolean }) =>
  cn(
    "rounded-md px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
    isActive && "bg-neutral-100 text-neutral-950"
  );

export const AppShell = ({ children, user }: AppShellProps) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const signOut = async () => {
    await authClient.signOut();
    setMobileOpen(false);
    navigate("/sign-in", { replace: true });
  };

  return (
    <div className="isolate min-h-dvh bg-background">
      <header className="sticky top-0 z-40 bg-white/95 ring-1 ring-neutral-950/5 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[100rem] items-center px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 flex-1 items-center">
            <Link
              aria-label="Homepage"
              className="flex min-w-0 items-center gap-2.5 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              to={user ? "/projects" : "/sign-in"}
            >
              <span
                aria-hidden="true"
                className="inline-grid size-8 shrink-0 place-items-center rounded-md bg-primary text-sm font-semibold text-white"
              >
                W
              </span>
              <span className="truncate text-sm font-semibold tracking-tight text-neutral-950">
                WAW Board
              </span>
            </Link>
          </div>

          {user ? (
            <nav aria-label="Main navigation" className="hidden items-center lg:flex">
              <NavLink className={navClassName} to="/projects">
                Projects
              </NavLink>
            </nav>
          ) : null}

          <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
            {user ? (
              <>
                <div className="hidden min-w-0 items-center gap-2 sm:flex">
                  <UserAvatar className="size-7" image={user.image} name={user.name} />
                  <p className="max-w-36 truncate text-sm text-neutral-600">@{user.username}</p>
                </div>
                <Button className="hidden lg:inline-flex" onClick={signOut} size="sm" type="button" variant="ghost">
                  Sign out
                </Button>
                <Button
                  aria-expanded={mobileOpen}
                  aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
                  className="lg:hidden"
                  onClick={() => setMobileOpen((value) => !value)}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  {mobileOpen ? (
                    <X className="size-4 shrink-0 stroke-current" />
                  ) : (
                    <Menu className="size-4 shrink-0 stroke-current" />
                  )}
                </Button>
              </>
            ) : null}
          </div>
        </div>

        {user && mobileOpen ? (
          <nav aria-label="Mobile navigation" className="border-t border-neutral-950/5 bg-white px-4 py-3 lg:hidden">
            <ul className="flex flex-col gap-1" role="list">
              <li>
                <NavLink className={navClassName} onClick={() => setMobileOpen(false)} to="/projects">
                  Projects
                </NavLink>
              </li>
              <li className="flex items-center justify-between gap-3 border-t border-neutral-950/5 pt-3">
                <div className="flex min-w-0 items-center gap-2">
                  <UserAvatar className="size-7" image={user.image} name={user.name} />
                  <p className="truncate text-base text-neutral-600 sm:text-sm">@{user.username}</p>
                </div>
                <Button onClick={signOut} size="sm" type="button" variant="ghost">
                  Sign out
                </Button>
              </li>
            </ul>
          </nav>
        ) : null}
      </header>

      <main>{children}</main>
    </div>
  );
};
